import { BORDER_RADIUS } from "@/constants/layout";
import { Box, Collapse, IconButton, Paper, Typography } from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import { ReactNode } from "react";

/**
 * Simple bordered card section for content grouping.
 * Use instead of ShowAccordionSection when you want always-expanded, cleaner sections.
 *
 * Supports an optional collapsible mode via `collapsible` + `isExpanded` + `onToggle`.
 * When collapsible, the header becomes clickable and a chevron is rendered.
 */
interface SectionCardProps {
  title?: string;
  subtitle?: string;
  children: ReactNode;
  action?: ReactNode;
  noPadding?: boolean;
  noDivider?: boolean;
  /** Enable collapsible behavior. Requires `isExpanded` and `onToggle`. */
  collapsible?: boolean;
  /** Controlled expanded state. Only used when `collapsible` is true. */
  isExpanded?: boolean;
  /** Toggle callback. Only used when `collapsible` is true. */
  onToggle?: () => void;
  /** Badge or count displayed next to the title (e.g., record count). */
  badge?: ReactNode;
  /** Compact mode — reduces padding and bottom margin for dense layouts. */
  compact?: boolean;
}

export const SectionCard = ({
  title,
  subtitle,
  children,
  action,
  noPadding = false,
  noDivider = false,
  collapsible = false,
  isExpanded = true,
  onToggle,
  badge,
  compact = false,
}: SectionCardProps) => {
  const showHeader = title || collapsible;
  const showDivider = !noDivider && (collapsible ? isExpanded : true);

  const headerPx = compact ? 2 : 3;
  const headerPy = compact ? 1.25 : 2;
  const contentP = noPadding ? 0 : compact ? 2 : 3;

  return (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: compact ? BORDER_RADIUS.LARGE : BORDER_RADIUS.XL,
        overflow: "hidden",
        mb: compact ? 1.5 : 3,
      }}
    >
      {showHeader && (
        <Box
          onClick={collapsible ? onToggle : undefined}
          sx={{
            px: headerPx,
            py: headerPy,
            borderBottom: showDivider ? 1 : 0,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            ...(collapsible && {
              cursor: "pointer",
              userSelect: "none",
              "&:hover": { opacity: 0.8 },
            }),
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {collapsible && (
              <IconButton size="small" sx={{ ml: -0.5 }}>
                {isExpanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
              </IconButton>
            )}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant={compact ? "body2" : "subtitle1"} fontWeight={600}>
                  {title}
                </Typography>
                {badge}
              </Box>
              {subtitle && (
                <Typography variant={compact ? "caption" : "body2"} color="text.secondary" sx={{ mt: 0.25 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
          {action && !collapsible && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box>{action}</Box>
            </Box>
          )}
          {action && collapsible && (
            <Box onClick={(e) => e.stopPropagation()} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box>{action}</Box>
            </Box>
          )}
        </Box>
      )}

      {collapsible ? (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ p: contentP }}>{children}</Box>
        </Collapse>
      ) : (
        <Box sx={{ p: contentP }}>{children}</Box>
      )}
    </Paper>
  );
};

/**
 * Info row for displaying label/value pairs in a clean list format.
 * Works great inside SectionCard or EditableGrid alternatives.
 */
interface InfoRowProps {
  label: string;
  value: string | number | ReactNode;
  divider?: boolean;
  /** Compact mode — tighter padding and smaller text. */
  compact?: boolean;
}

export const InfoRow = ({ label, value, divider = true, compact = false }: InfoRowProps) => {
  return (
    <Box
      sx={{
        py: compact ? 1 : 2,
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: 0.5, sm: compact ? 1.5 : 2 },
        alignItems: { xs: "flex-start", sm: "center" },
        borderBottom: divider ? 1 : 0,
        borderColor: "divider",
        "&:last-child": {
          borderBottom: 0,
        },
      }}
    >
      <Typography
        variant={compact ? "caption" : undefined}
        color="text.secondary"
        sx={{
          fontWeight: 500,
          minWidth: { sm: compact ? "120px" : "140px" },
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>
      <Typography variant={compact ? "body2" : undefined} sx={{ fontWeight: 500, wordBreak: "break-word" }}>
        {value}
      </Typography>
    </Box>
  );
};
